import axios from "axios";
import Cookies from "js-cookie";
import type {
  ICreateTemplateCredentials,
  ICreateTemplateResponse,
  IDeleteDocumenentsTemplatesCredentials,
  IDeleteDocumenentsTemplatesResponse,
  IExportTemplatesToExcelParams,
  IGetAllTemplatesParams,
  IGetAllTemplatesResponse,
  IGetAllDocumentsTypesResponse,
  IGetTemplateByIdParams,
  IGetTemplateByIdResponse,
  IUpdateTemplateCredentials,
  IUpdateTemplateResponse,
  IUpdateDocumentTemplateByIdBody,
  IUpdateDocumentTemplateByIdResponse,
} from "../shared/types/templates.types";
import type {
  IGetAllDocumentsParams,
  IGetAllDocumentsResponse,
  IGetDocumentByIdParams,
  IGetDocumentByIdResponse,
  IGetTemporaryLinkToFileParams,
  IGetTemporaryLinkToFileResponse,
  IUpdateDocumentByIdBody,
  IUpdateDocumentByIdParams,
} from "../shared/types/documents.types";
import type {
  ILoginCredentials,
  ILoginResponse,
  IRefreshCredentials,
  IRefreshResponse,
  IRegisterCredentials,
  IRegisterResponse,
} from "../shared/types/auth.types";
import type {
  IGetNotificationsParams,
  IGetNotificationsResponse,
  IPatchNotificationBody,
  IPatchNotificationsBatchBody,
  IPatchNotificationsBatchResponse,
} from "../shared/types/notifications.types";
import type {
  GetAllDepartmentsListType,
  ICreateDepartmentBody,
  ICreateDepartmentResponse,
  IDeleteDepartmentsBody,
  IDeleteDepartmentsResponse,
  IGetAllDepartmentsParams,
  IGetAllDepartmentsResponse,
} from "../shared/types/departments.types";
import {
  hideLoader,
  showLoader,
} from "../shared/Contexts/Loader/loaderController";
import type {
  AnalyzeKeywordsResponseType,
  ExtractDataResponseType,
  GetTemplateResultType,
} from "../shared/types/files.types";

class ApiClient {
  private async withLoader<T>(fn: () => Promise<T>): Promise<T> {
    showLoader();
    try {
      return await fn();
    } catch (error) {
      this.handleError(error);
    } finally {
      hideLoader();
    }
  }

  private axiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  constructor() {
    this.axiosInstance.interceptors.request.use((config) => {
      const token = Cookies.get("access_token");
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  private handleError(error: unknown): never {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        throw new Error(
          `API Error: ${error.response.status} - ${JSON.stringify(
            error.response.data
          )}`
        );
      }
      if (error.request) {
        throw new Error("API Error: No response received from server");
      }
      throw new Error(`API Error: ${error.message}`);
    } else if (error instanceof Error) {
      // Не axios ошибка, но стандартный объект Error
      throw error;
    } else {
      // Неизвестный тип ошибки (string, number и т.п.)
      throw new Error(String(error));
    }
  }

  // --- AUTH ---

  async register(
    credentials: IRegisterCredentials
  ): Promise<IRegisterResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.post(
          "/register",
          credentials
        );
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async login(credentials: ILoginCredentials): Promise<ILoginResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.post("login", credentials);
        // Сохраняем токен в cookie
        Cookies.set("access_token", data.access_token);
        Cookies.set("refresh_token", data.refresh_token);
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async refresh(credentials: IRefreshCredentials): Promise<IRefreshResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.post("/refresh", credentials);
        Cookies.set("access_token", data.access_token);
        Cookies.set("refresh_token", data.refresh_token);
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  // --- DOCUMENTS ---

  async getAllDocuments(
    params?: IGetAllDocumentsParams
  ): Promise<IGetAllDocumentsResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.get("/documents", { params });
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async getDocumentById(
    params: IGetDocumentByIdParams
  ): Promise<IGetDocumentByIdResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.get(
          `/documents/${params.id}`
        );
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async updateDocumentById({
    params,
    body,
  }: {
    params: IUpdateDocumentByIdParams;
    body: IUpdateDocumentByIdBody;
  }): Promise<IGetAllDocumentsResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.patch(
          `/documents/${params.id}`,
          body
        );
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  // --- DOCUMENT TYPES (TEMPLATES) ---

  async getAllDocumentsTypes(): Promise<IGetAllDocumentsTypesResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.get("/document-types/all");
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async TemplategetAllTemplates(
    params?: IGetAllTemplatesParams
  ): Promise<IGetAllTemplatesResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.get("/document-types", {
          params,
        });
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async createTemplate(
    credentials: ICreateTemplateCredentials
  ): Promise<ICreateTemplateResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.post(
          "/document-types",
          credentials
        );
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async updateTemplate(
    credentials: IUpdateTemplateCredentials
  ): Promise<IUpdateTemplateResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.patch(
          "/document-types",
          credentials
        );
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async deleteTemplates(
    credentials: IDeleteDocumenentsTemplatesCredentials
  ): Promise<IDeleteDocumenentsTemplatesResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.delete("/document-types", {
          data: credentials,
        });
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async getTemplateById(
    params: IGetTemplateByIdParams
  ): Promise<IGetTemplateByIdResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.get(
          `/document-types/${params.id}`
        );
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async updateDocumentTemplateById(
    body: IUpdateDocumentTemplateByIdBody
  ): Promise<IUpdateDocumentTemplateByIdResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.patch(
          "/document-types/change-type",
          body
        );
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  // OCR && Other Files Handlers
  async preSignFile(
    file: File
  ): Promise<{ url: string; key: string; documentId: string }> {
    try {
      // 1. Получить pre-signed URL
      const { data } = await this.axiosInstance.post("/file/presign/v2", {
        filename: file.name,
        contentType: file.type,
      });

      return data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async uploadPreSignDataToS3(
    file: File,
    preSignDataUrl: string
  ): Promise<void> {
    try {
      await axios.put(preSignDataUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async uploadOcrFile(presignedId: string): Promise<void> {
    try {
      const { data: confirmData } = await this.axiosInstance.post(
        "/file/confirm-upload/v2",
        {
          documentId: presignedId,
        }
      );

      return confirmData;
    } catch (error) {
      this.handleError(error);
    }
  }

  async exportTemplatesToExcel(
    params: IExportTemplatesToExcelParams
  ): Promise<Blob> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.get<Blob>(
          "/file/export-file",
          {
            params,
            responseType: "blob",
          }
        );
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async exportFilesToExcel(params: {
    dateFrom: string | undefined;
    dateTo: string | undefined;
  }): Promise<Blob> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.get<Blob>(
          "/file/export-documents",
          {
            params,
            responseType: "blob",
          }
        );
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async getTemporaryLinkToFile(
    params: IGetTemporaryLinkToFileParams
  ): Promise<IGetTemporaryLinkToFileResponse> {
    return this.withLoader(async () => {
      try {
        const { data } =
          await this.axiosInstance.get<IGetTemporaryLinkToFileResponse>(
            "/file/tempered-link",
            {
              params,
            }
          );
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async extractData(file: File): Promise<ExtractDataResponseType> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await this.axiosInstance.post<ExtractDataResponseType>(
        "/document-types/template/extract-data",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getTemplateResult(jobId: string): Promise<GetTemplateResultType> {
    try {
      const { data } = await this.axiosInstance.get<GetTemplateResultType>(
        `/document-types/template-result/${jobId}`
      );
      return data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async analyzeKeywords({
    text,
    keywords,
  }: {
    text: string;
    keywords: string[];
  }): Promise<AnalyzeKeywordsResponseType> {
    return this.withLoader(async () => {
      try {
        let body;
        if (keywords.length > 0) {
          body = {
            keywords,
            text,
          };
        } else {
          body = {
            text,
          };
        }
        const { data } =
          await this.axiosInstance.post<AnalyzeKeywordsResponseType>(
            "/document-types/keywords/analyze",
            { ...body }
          );
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  // OCR && Other Files Handlers

  // Notifications
  async getNotifications(
    params: IGetNotificationsParams
  ): Promise<IGetNotificationsResponse> {
    try {
      const { data } = await this.axiosInstance.get("/notifications", {
        params,
      });

      return data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async markAllNotificationsAsRead(): Promise<void> {
    return this.withLoader(async () => {
      try {
        await this.axiosInstance.patch("/notifications/read-all");
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async updateNotification(
    body: IPatchNotificationBody
  ): Promise<IGetNotificationsResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.patch("/notifications", body);

        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async updateNotificationsBatch(
    body: IPatchNotificationsBatchBody
  ): Promise<IPatchNotificationsBatchResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.patch(
          "/notifications/batch",
          body
        );

        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }
  // Notifications

  // Departments
  async getAllDepartments(
    params?: IGetAllDepartmentsParams
  ): Promise<IGetAllDepartmentsResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.get("/departments", {
          params,
        });
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async getAllDepartmentsList(): Promise<GetAllDepartmentsListType> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.get("/departments/all");
        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async createDepartment(
    body: ICreateDepartmentBody
  ): Promise<ICreateDepartmentResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.post("/departments", body);

        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }

  async deleteDepartments(
    body: IDeleteDepartmentsBody
  ): Promise<IDeleteDepartmentsResponse> {
    return this.withLoader(async () => {
      try {
        const { data } = await this.axiosInstance.delete("/departments", {
          data: body,
        });

        return data;
      } catch (error) {
        this.handleError(error);
      }
    });
  }
  // Departments

  async startTestQueriesAsync({
    s3Key,
    queries,
  }: {
    s3Key: string;
    queries: { field: string; question: string }[];
  }): Promise<{ jobId: string; status: string }> {
    try {
      const { data } = await this.axiosInstance.post(
        "/document-types/template/test-queries-v2",
        {
          s3Key,
          queries,
        }
      );
      return data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getTestQueriesResult(jobId: string): Promise<{
    status: string;
    result: { query: string; answer: string; confidence: number }[];
  }> {
    try {
      const { data } = await this.axiosInstance.get(
        `/document-types/template/test-queries-v2/${jobId}`
      );
      return data;
    } catch (error) {
      this.handleError(error);
    }
  }
}

const api = new ApiClient();

export default api;
